import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getBlogPosts, BlogPost } from '../services/api';
import { getCurrentDomain } from '../config/domains';

// Workaround limit until server-side tag filtering is implemented (see planned improvements).
const TAG_FILTER_FETCH_LIMIT = 500;

const BlogByTag: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();
  const domain = getCurrentDomain();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const response = await getBlogPosts(TAG_FILTER_FETCH_LIMIT);
      if (response.error) {
        setError(response.error);
      } else {
        // Filter posts by tag on the frontend
        const allPosts = response.data?.posts || [];
        const filteredPosts = allPosts.filter(post =>
          post.tags && post.tags.includes(tag || '')
        );
        setPosts(filteredPosts);
      }
      setLoading(false);
    };

    fetchPosts();
  }, [tag]);

  if (loading) {
    return <div className="loading">Loading posts tagged "{tag}"...</div>;
  }

  if (error) {
    return <div className="error">Error loading blog posts: {error}</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <p className="page-content" style={{ fontSize: '1.1rem', marginBottom: '0' }}>
          Posts tagged with <strong style={{ color: domain.primaryColor }}>"{tag}"</strong>
        </p>
      </div>

      <div className="blog-list">
        {posts.length === 0 ? (
          <p>No blog posts found with this tag.</p>
        ) : (
          posts.map((post) => (
            <article key={post.id} className="blog-post-card">
              <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                <h2 className="blog-post-title" style={{ color: domain.primaryColor }}>
                  {post.title}
                </h2>
              </Link>
              <div className="blog-post-meta">
                By {post.author} on {new Date(post.published_date).toLocaleDateString()}
              </div>
              <p className="blog-post-excerpt">{post.excerpt}</p>
              {post.tags && post.tags.length > 0 && (
                <div className="blog-tags">
                  {post.tags.map((postTag) => (
                    <Link
                      key={postTag}
                      to={`/blog/tag/${encodeURIComponent(postTag)}`}
                      className={`blog-tag blog-tag-link ${postTag === tag ? 'blog-tag-active' : ''}`}
                      style={{ textDecoration: 'none' }}
                    >
                      {postTag}
                    </Link>
                  ))}
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogByTag;
